import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link, Navigate, useLocation } from 'react-router-dom'
import { GoogleLogin } from '@react-oauth/google'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAuth } from '@/context/AuthContext'
import { useRedirectTarget } from '@/hooks/useRedirectTarget'

const schema = z.object({
  email: z.string().email('有効なメールアドレスを入力してください'),
  password: z.string().min(1, 'パスワードを入力してください'),
})

type FormValues = z.infer<typeof schema>

export default function LoginPage() {
  const { login, googleLogin, isAuthenticated, isLoading: authLoading } = useAuth()
  const location = useLocation()
  const from = useRedirectTarget()
  const [isLoading, setIsLoading] = useState(false)

  const { register, handleSubmit, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
  })

  // 認証状態の確定待ち（トークン保持者の一瞬のフォームちらつきを防ぐ）。
  if (authLoading) return null
  // 認証済みになったら宣言的に遷移する（imperative navigate のタイミング競合を排除）。
  // 全 Hook 呼び出しの後に置く（Hooks 規約）。
  if (isAuthenticated) return <Navigate to={from} replace />

  const onSubmit = async (values: FormValues) => {
    setIsLoading(true)
    try {
      await login(values.email, values.password)
      // 遷移は isAuthenticated を見た宣言的リダイレクトに任せる
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg ?? 'ログインに失敗しました')
    } finally {
      setIsLoading(false)
    }
  }

  const handleGoogleSuccess = async (credential: string) => {
    try {
      await googleLogin(credential)
      // 遷移は isAuthenticated を見た宣言的リダイレクトに任せる
    } catch (e: unknown) {
      const msg = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(msg ?? 'Googleログインに失敗しました')
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-gradient-to-br from-blue-400 via-purple-400 to-pink-500">
      <div className="w-full max-w-sm space-y-6">
        {/* ヒーロー領域 */}
        <div className="text-center space-y-2">
          <img src="/512.png" alt="LIFE TROPHY" className="w-28 h-28 mx-auto drop-shadow-lg" />
          <h1 className="text-3xl font-bold text-white tracking-wide drop-shadow">LIFE TROPHY</h1>
          <p className="text-white/80 text-sm">人生の実績を刻もう</p>
        </div>

        {/* フォームカード */}
        <div className="bg-white/85 backdrop-blur-sm rounded-2xl shadow-xl p-6 space-y-4">
          <div className="flex justify-center scale-90 origin-center">
            <GoogleLogin
              onSuccess={(res) => {
                if (res.credential) handleGoogleSuccess(res.credential)
              }}
              onError={() => toast.error('Googleログインに失敗しました')}
              width={360}
              text="signin_with"
            />
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-gray-200" />
            <span className="text-xs text-gray-400">または</span>
            <div className="flex-1 h-px bg-gray-200" />
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">メールアドレス</label>
              <Input {...register('email')} type="email" placeholder="example@email.com" autoComplete="email" />
              {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
            </div>

            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700">パスワード</label>
              <Input {...register('password')} type="password" placeholder="パスワード" autoComplete="current-password" />
              {errors.password && <p className="text-xs text-destructive">{errors.password.message}</p>}
            </div>

            <Button
              type="submit"
              className="w-full bg-gradient-to-r from-blue-500 to-pink-500 hover:from-blue-600 hover:to-pink-600 text-white border-0"
              disabled={isLoading}
            >
              {isLoading ? 'ログイン中...' : 'ログイン'}
            </Button>
          </form>

          <p className="text-center text-sm text-gray-500">
            アカウントをお持ちでない方は{' '}
            <Link to="/register" state={location.state} className="text-purple-600 underline underline-offset-2 font-medium">
              新規登録
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
